import parse, {
  type DOMNode,
  domToReact,
  Element,
  type HTMLReactParserOptions,
} from "html-react-parser";
import Link from "next/link";
import type { HTMLAttributes } from "react";
import React from "react";
import Markdown from "react-markdown";
// @ts-expect-error No types provided
import { Prism } from "react-syntax-highlighter";
// @ts-expect-error No types provided
import darcula from "react-syntax-highlighter/dist/esm/styles/prism/darcula";
// @ts-expect-error No types provided
import oneLight from "react-syntax-highlighter/dist/esm/styles/prism/one-light";
import remarkGfm from "remark-gfm";
import sanitizeHtml from "sanitize-html";

import { EditorMediaType } from "@/components/app/editor-v3/constants";
import { AccountSocialCard } from "@/components/shared/account-social-card";
import CopyToClipboard from "@/components/shared/copy-to-clipboard";
import { PostImageSection } from "@/components/shared/post-image/post-image-section";
import { Button } from "@/components/ui/button";
import type { PostType } from "@/contexts/feed-context";
import { useOwnTheme } from "@/contexts/theme-context";
import { isValidMediaDimension } from "@/utils/media-dimensions";
import type { MediaItem } from "@/utils/media-utils";
import type {
  AudioMetadata,
  ImageMetadata,
  VideoMetadata,
} from "@/utils/media-utils";
import type { Maybe, PartialAccount, PostMediaMeta } from "@/utils/types";

import { URL_REGEXP } from "./constants";
import { getUserName } from "./usernames";

const prismDark = structuredClone(darcula);
const prismLight = structuredClone(oneLight);

[prismDark, prismLight].forEach((theme) => {
  theme[':not(pre) > code[class*="language-"]'].background = "transparent";
  theme['pre[class*="language-"]'].background = "transparent";
  theme['code[class*="language-"]'].background = "transparent";
});

// Only include the last 18 characters of the URL to avoid saving the
// full path to gated contents
export const getMediaClientIdFromUrl = (url: string) => {
  return url.slice(-18);
};

export const getMediaMetaFromUrl = (
  url: string,
  mediaMeta: PostMediaMeta = {},
) => {
  const clientId = getMediaClientIdFromUrl(url);
  const key = Object.keys(mediaMeta).find((key) => key.endsWith(clientId));

  if (!key) return null;

  return mediaMeta[key];
};

export const getUrlFromClientId = (clientId: string, urls: string[]) => {
  return urls.find((url) => url.endsWith(clientId));
};

const MEDIA_TYPE_REGEXP =
  /^(image|inline_image|gated_image|video|gated_video|audio|gated_audio)\.(\d+)\.(\w+)/;

const parsePositiveMetadataInt = (value: string) => {
  const parsed = Number.parseInt(value, 10);

  return isValidMediaDimension(parsed) ? parsed : undefined;
};

const parseFiniteMetadataFloat = (value: string) => {
  const parsed = Number.parseFloat(value);

  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
};

export const buildMediaMeta = (
  extraData: Record<string, string>,
): PostMediaMeta => {
  return Object.entries(extraData).reduce((acc, [key, value]) => {
    const match = MEDIA_TYPE_REGEXP.exec(key);
    if (!match) return acc;

    const [, type, index, prop] = match;
    const clientId = extraData[`${type}.${index}.clientId`];

    // @ts-expect-error stop worrying about it
    const o = (acc[clientId] ??= {
      type,
    });

    switch (prop) {
      case "width":
      case "height":
        {
          const parsed = parsePositiveMetadataInt(value);

          if (parsed !== undefined) {
            o[prop] = parsed;
          }
        }
        break;
      case "duration":
        {
          const parsed = parseFiniteMetadataFloat(value);

          if (parsed !== undefined) {
            o[prop] = parsed;
          }
        }
        break;
      case "avgColor":
        try {
          o[prop] = JSON.parse(value);
        } catch {
          // Ignore malformed legacy metadata.
        }
        break;
      case "orientation":
        if (value === "landscape" || value === "portrait") {
          o[prop] = value;
        }
        break;
      default:
        o[prop] = value;
    }

    return acc;
  }, {});
};

export const getPostData = (post: PostType) => {
  const repost = post.repostedPost;
  const isQuotedRepost = post.isQuotedRepost;
  const timestamp = post.timestamp;
  const isPureRepost = !!repost && !isQuotedRepost;
  const { poster, body, imageUrls, videoUrls } = isPureRepost ? repost : post;
  const { username, publicKey } = poster ?? {};

  const innerPost = isPureRepost ? repost.repostedPost : repost;

  const isPostRemoved = !!post.isHidden;
  const isReply = !!post.parentPost?.postHash;
  const replyRecipient = post.parentPost?.poster;

  const isParentPostRemoved = !!post.parentPost?.isHidden;
  const reactionsCount = post.postStats?.totalReactionCount || 0;
  const repostReactionsCount =
    post?.repostedPost?.postStats?.totalReactionCount || 0;
  const mediaMeta = buildMediaMeta(
    isPureRepost ? repost?.extraData : post.extraData,
  );

  return {
    isReply,
    replyRecipient,
    videoUrls: videoUrls?.filter(Boolean),
    isPostRemoved,
    isParentPostRemoved,
    isPureRepost,
    body: body as string,
    imageUrls: imageUrls?.filter(Boolean) as string[],
    poster,
    repost,
    username: username as string,
    publicKey: publicKey as string,
    innerPost,
    timestamp,
    reactionsCount,
    repostReactionsCount,
    mediaMeta,
  };
};

export const generateMediaMetadata = (list: MediaItem[], key: string) => {
  const acc: Record<string, string> = {};
  return list.reduce((acc, media, index) => {
    if (
      media.type === EditorMediaType.Image ||
      media.type === EditorMediaType.Video
    ) {
      const metadata = media.metadata as ImageMetadata | VideoMetadata;

      if (isValidMediaDimension(metadata?.width)) {
        acc[`${key}.${index}.width`] = metadata.width.toString();
      }
      if (isValidMediaDimension(metadata?.height)) {
        acc[`${key}.${index}.height`] = metadata.height.toString();
      }
      if (metadata?.orientation) {
        acc[`${key}.${index}.orientation`] = metadata.orientation;
      }
      if (metadata?.avgColor) {
        acc[`${key}.${index}.avgColor`] = JSON.stringify(metadata.avgColor);
      }
    } else if (media.type === EditorMediaType.Audio) {
      const metadata = media.metadata as AudioMetadata;
      acc[`${key}.${index}.duration`] = metadata?.duration?.toString() ?? "";
      acc[`${key}.${index}.title`] = metadata?.title ?? "";
    }
    acc[`${key}.${index}.clientId`] = getMediaClientIdFromUrl(
      media.metadata?.url ?? "",
    );
    return acc;
  }, acc);
};

export const getPostDiamondValueAndCount = (post?: Maybe<PostType>) => {
  return {
    diamondsCount: parseInt(post?.diamondTotals?.diamondCount ?? "0"),
    diamondsValueUSD: Number(post?.diamondTotals?.usdCentsSum ?? "0") / 100,
    diamondsValueUsdCents: parseInt(post?.diamondTotals?.usdCentsSum ?? "0"),
  };
};

const CODEBLOCK_REGEX = /```[\s\S]*?```/g;
const USERNAME_REGEX = /(?<=^|\s)[@$][\w,\\]+/g;
const DOLLAR_VALUE_REGEX = /^\$\d+([,.]\d+)?([kKmMbB])?$/;
const HASHTAG_REGEX = /\B(#[a-zA-Z0-9]+\b)(?!;)/g;
export const AUDIO_ONLY_TAG = "[[[audio-only]]]";
const BR_REGEX = /<br\s*\/?>/gi;

const getHashtagSearchHref = (hashtag: string) => {
  const searchTerm = hashtag.startsWith("#") ? hashtag.slice(1) : hashtag;

  return `/search?search=${encodeURIComponent(searchTerm)}`;
};

export const replaceCodeBlockWithPlaceholder = (
  content: string,
): [string, (content: string) => string] => {
  const codeBlockCache: Record<string, string> = {};

  return [
    content.replace(CODEBLOCK_REGEX, (codeBlock) => {
      const id = `<<<${crypto.randomUUID()}>>>`;
      codeBlockCache[id] = codeBlock;
      return id;
    }),
    // replacePlaceholderWithCodeBlock
    (content: string) => {
      return Object.entries(codeBlockCache).reduce((acc, [id, codeBlock]) => {
        return acc.replace(id, codeBlock);
      }, content);
    },
  ];
};

export const processRawPostContentToMDContent = (
  content: string,
  affectedUsers: string[] = [],
) => {
  if (content === AUDIO_ONLY_TAG) return "";

  // We remove code blocks from any customization, we need to preserve code as is
  const [contentWithoutCodeBlocks, replacePlaceholderWithCodeBlock] =
    replaceCodeBlockWithPlaceholder(content);

  let results = contentWithoutCodeBlocks;

  // Handle usernames
  if (affectedUsers.length > 0) {
    const affectedUsersRegexp = new RegExp(
      `[$@](${affectedUsers.join("|")})`,
      "gi",
    );
    results = results.replace(affectedUsersRegexp, (match) => {
      return `[${match}](/${match.slice(1)})`;
    });
  }

  results = results
    .replace(BR_REGEX, "")
    // Handle hashtags
    .replace(
      HASHTAG_REGEX,
      (match: string) => `[${match}](${getHashtagSearchHref(match)})`,
    )
    // Handle usernames (legacy, remove this if `affectedUsers` will start to work)
    .replace(USERNAME_REGEX, (match) => {
      // If it's a dollar value, skip it
      // We could have used a single reqexp to match both, but it
      // would require negative lookahead, which is slow on large bodies.
      if (DOLLAR_VALUE_REGEX.test(match)) {
        return match;
      }

      if (match.endsWith(",")) {
        return `[${match.slice(0, -1)}](/${match.slice(1, -1)}),`;
      }

      return `[${match}](/${match.slice(1)})`;
    })
    // Add extra line breaks for images to overcome of some users
    // not using 2 spaces before an image
    // We might not need this anymore since images are changed to be displayed as block
    // instead of inline, but I'm keeping it here for future reference
    /*.replaceAll(/^!\[([^\]]*)]\(([^)]+)\)$/gm, (match) => {
      return `\n\n${match}\n\n`;
    })*/
    .trim()
    // Remove hash escapes
    .replaceAll("\\[#", "[#")
    // Replace escaped and entity newlines
    .replace(/(\\\n|&#x20;)/g, "\n");

  return replacePlaceholderWithCodeBlock(results);
};

export const processRawPostContentToHTMLContent = (content?: string) => {
  if (!content) return "";

  const classNames = `
    font-normal text-primary no-underline underline-offset-4
    hover:text-primary hover:underline
  `;
  const linkClass = `class='${classNames}'`;
  const processedContent = content
    .replace(AUDIO_ONLY_TAG, "")
    .replace(USERNAME_REGEX, (match) => {
      const name = match.trim().slice(1);

      // would require negative lookahead, which is slow on large bodies.
      if (DOLLAR_VALUE_REGEX.test(match)) {
        return match;
      }

      return ` <a href="/${name}" ${linkClass}>${match.trim()}</a>`;
    })
    .replace(URL_REGEXP, (url) => {
      // Check if url starts with http:// or https://
      if (!url.startsWith("http://") && !url.startsWith("https://")) {
        // If not, add http:// at the start
        url = "http://" + url;
      }

      const internalLink = url?.startsWith(process.env.BASE_URL);
      const finalHref = internalLink
        ? url?.replace(process.env.BASE_URL, "")
        : url;

      const rel = internalLink ? "" : `rel="nofollow noopener noreferrer"`;

      return `<a href="${finalHref}" ${linkClass} ${rel}>${finalHref}</a>`;
    })
    .replace(
      HASHTAG_REGEX,
      (match) =>
        `<a href="${getHashtagSearchHref(match)}" ${linkClass}>${match}</a>`,
    )
    // Replace shift enter with normal newlines
    .replaceAll("\\\n", "\n")
    // Only allow maximum 2 sequential newlines
    .replaceAll(/\n{3,}/g, "\n\n");

  const parserOptions: HTMLReactParserOptions = {
    replace(domNode) {
      if (!(domNode instanceof Element) || domNode.name !== "a") return;

      return (
        <PostLink href={domNode.attribs.href}>
          {domToReact(domNode.children as DOMNode[], parserOptions)}
        </PostLink>
      );
    },
  };
  const sanitizedContent = sanitizeHtml(processedContent, {
    allowedClasses: {
      a: classNames.split(" "),
      span: classNames.split(" "),
    },
  });

  return (
    <p className="w-full min-w-0 wrap-break-word whitespace-pre-wrap">
      {parse(sanitizedContent, parserOptions)}
    </p>
  );
};

export const MIN_CHAR_TO_SHOW = 700;

export const smartPostBodyCut = (
  text: string,
  maxLength: number = MIN_CHAR_TO_SHOW,
) => {
  if (text.length <= maxLength) return text;
  let cutPoint = text.lastIndexOf("\n", maxLength);
  if (cutPoint === -1) cutPoint = maxLength;
  return text.substring(0, cutPoint);
};

export const extractImagesFromMarkdown = (markdown: string) => {
  const imageUrls: string[] = [];
  const imageRegex = /!\[.*?]\((.*?)\)/g;
  let match;

  while ((match = imageRegex.exec(markdown)) !== null) {
    if (match[1]) {
      imageUrls.push(match[1]);
    }
  }

  return imageUrls;
};

const PostLink = ({
  href,
  children,
}: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
  const classList =
    "inline font-normal text-primary no-underline hover:text-primary hover:underline underline-offset-4";
  const internalLink =
    href?.startsWith("/") || href?.startsWith(process.env.BASE_URL);
  const finalHref = internalLink
    ? href?.replace(process.env.BASE_URL, "")
    : href;

  if (
    internalLink &&
    typeof children === "string" &&
    (children?.startsWith("@") || children?.startsWith("$"))
  ) {
    const username = finalHref?.slice(1);

    if (username && finalHref) {
      return (
        <AccountSocialCard username={username}>
          <Link href={finalHref} className={classList} prefetch={false}>
            {children}
          </Link>
        </AccountSocialCard>
      );
    }
  }

  return finalHref ? (
    <Link
      href={finalHref}
      className={classList}
      target={internalLink ? undefined : "_blank"}
      rel={internalLink ? undefined : "nofollow noopener noreferrer"}
      prefetch={false}
    >
      {children}
    </Link>
  ) : (
    <span className={classList}>{children}</span>
  );
};

const markdownComponents = {
  a: PostLink,
  img: ({ src }: React.ImgHTMLAttributes<HTMLImageElement>) => {
    return src ? (
      <div className="inline-image py-1 align-middle">
        <PostImageSection
          urls={[src as string]}
          optimize={(src as string).includes("images.deso.org")}
        />
      </div>
    ) : null;
  },
  p: ({ children }: HTMLAttributes<HTMLParagraphElement>) => (
    <div
      className="
        w-full min-w-0 wrap-break-word
        [&:has(code.inline)]:pt-1
      "
    >
      {children}
    </div>
  ),
  // @ts-expect-error wrong 3rd party types
  pre: ({ children, node }: HTMLAttributes<HTMLElement>) => {
    node.children[0].properties.block = true;
    return (
      <div
        suppressHydrationWarning
        className="
          markdown-prism custom-scrollbar-all custom-scrollbar-hover-only
          max-h-[600px] w-full overflow-auto rounded-2xl border
          border-border-light bg-card text-foreground
        "
      >
        {children}
      </div>
    );
  },
  code(
    props: HTMLAttributes<HTMLElement> & {
      // eslint-disable-next-line @typescript-eslint/consistent-type-imports
      node?: import("hast").Element;
    },
  ) {
    const { children, className, node, ...rest } = props;

    if (!node?.properties?.block) {
      return (
        <code
          className="
            inline border border-border-light bg-card box-decoration-clone px-1
            py-px font-mono text-sm font-normal text-muted-foreground!
          "
        >
          {children}
        </code>
      );
    }

    const match = /language-(\w+)/.exec(className || "");
    const title = match?.[1] || "Code";
    const text = String(children).replace(/\n$/, "");
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { theme } = useOwnTheme();
    const styles = theme?.includes("dark") ? prismDark : prismLight;

    return (
      <>
        <section
          className="
            sticky top-0 left-0 flex w-full items-center p-3 backdrop-blur-sm
          "
        >
          <div className="font-bold text-foreground uppercase">{title}</div>
          <div className="ml-auto!">
            <CopyToClipboard
              as={Button}
              text={text}
              variant="outline"
              size="xs"
            >
              Copy
            </CopyToClipboard>
          </div>
        </section>
        <Prism
          {...rest}
          PreTag="div"
          language={match?.[1]}
          style={styles}
          //showLineNumbers
        >
          {text}
        </Prism>
      </>
    );
  },
};

export const getMarkdownBody = (body: string) => {
  return (
    <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
      {processRawPostContentToMDContent(body)}
    </Markdown>
  );
};

export interface PostGatedContentSchema {
  SubscriptionAccessGroupKeyName: string;
  SubscriptionAccessGroupOwnerPublicKey: string;
  SubscriptionAccessGroupPublicKey: string;
  SubscriptionEncryptedPostBody: string;
  SubscriptionEncryptedImageURLs: string;
  SubscriptionEncryptedVideoURLs: string;
  SubscriptionEncryptedAudioURLs: string;
  SubscriptionEncryptedPollWeightType?: string;
  SubscriptionEncryptedPollOptions?: string;
  SubscriptionEncryptedPollWeightTokenPublicKey?: string;
  SubscriptionBlurHash?: string;
  PriceToUnlockUsdCents: number;
}

export const parsePostGatedContent = (
  postExtraData: Maybe<Record<string, string>>,
): PostGatedContentSchema | null => {
  if (
    !(
      postExtraData &&
      postExtraData.SubscriptionAccessGroupKeyName &&
      postExtraData.SubscriptionAccessGroupOwnerPublicKey &&
      postExtraData.SubscriptionAccessGroupPublicKey &&
      (postExtraData.SubscriptionEncryptedPostBody ||
        postExtraData.SubscriptionEncryptedImageURLs ||
        postExtraData.SubscriptionEncryptedVideoURLs ||
        postExtraData.SubscriptionEncryptedAudioURLs ||
        postExtraData.SubscriptionEncryptedPollWeightType)
    )
  ) {
    return null;
  }

  return {
    SubscriptionAccessGroupKeyName:
      postExtraData!.SubscriptionAccessGroupKeyName,
    SubscriptionAccessGroupOwnerPublicKey:
      postExtraData!.SubscriptionAccessGroupOwnerPublicKey,
    SubscriptionEncryptedPostBody:
      postExtraData!.SubscriptionEncryptedPostBody ?? "",
    SubscriptionAccessGroupPublicKey:
      postExtraData!.SubscriptionAccessGroupPublicKey,
    SubscriptionEncryptedImageURLs:
      postExtraData!.SubscriptionEncryptedImageURLs ?? "",
    SubscriptionEncryptedVideoURLs:
      postExtraData!.SubscriptionEncryptedVideoURLs ?? "",
    SubscriptionEncryptedAudioURLs:
      postExtraData!.SubscriptionEncryptedAudioURLs ?? "",
    SubscriptionBlurHash: postExtraData!.SubscriptionBlurHash,
    SubscriptionEncryptedPollWeightType:
      postExtraData!.SubscriptionEncryptedPollWeightType,
    SubscriptionEncryptedPollOptions:
      postExtraData!.SubscriptionEncryptedPollOptions,
    SubscriptionEncryptedPollWeightTokenPublicKey:
      postExtraData!.SubscriptionEncryptedPollWeightTokenPublicKey,
    PriceToUnlockUsdCents: Number(postExtraData?.PriceToUnlockUsdCents ?? 0),
  };
};

export const appendPostBodyFooter = (body: string, account: PartialAccount) => {
  // NOTE: we insert the @ prefix here to ensure that any username link is supported, even if the username matches a reserved keyword like "wallet" or "discover"
  return `${body}\n\nTo view this post, subscribe to ${getUserName({ account })}'s feed at ${process.env.NEXT_PUBLIC_FOCUS_URI}/${getUserName({ account, customPrefix: "@" })}`;
};

export const stripPostBodyFooter = (body: string) => {
  // NOTE: we use a generic domain name regex here, as the domain name can change depending on the deployment
  // environment. This just ensures that we can strip the footer from the post body regardless of the domain name.
  return body.replace(
    /(\n\n)?To view this post, subscribe to (@|\$)\w+'s feed at https?:\/\/(.*)\/@?\w+$/,
    "",
  );
};

export enum ActivityModalType {
  reaction = "reaction",
  repost = "repost",
  quotedRepost = "quotedRepost",
  diamond = "diamond",
  view = "view",
}
